/*
 * <wc-tp-reviews>: the behaviour of blocks/wc-trustpilot-reviews (session
 * W6, 2026-09-24). The reviews themselves are in the HTML; this only:
 *
 * - DECIDES WHETHER THE SUMMARY IS FRESH. Liquid's `now` is cached with the
 *   page, so the server writes the summary line into an inert <template> and
 *   this puts it in the page only when its as_of (a UTC date-time) is at most
 *   72 hours old. Anything else, including an as_of that will not parse or
 *   lies more than an hour in the future, leaves it out. The root then says
 *   data-wc-tp-summary="shown" or "stale" (W2's contract, K1's check W-F).
 * - moves the row a page of cards at a time with the arrows, disables each
 *   arrow at its end, and hides both when every card already fits.
 * - SLIDES ON ITS OWN, a card every 8 s (EmbedSocial's Swiper moves cards,
 *   not pages), because Luke
 *   vetoed the still row (I-34, 2026-09-25: "we want the widget to slide on
 *   its own. hover can pause it, but no buttons for motion please"). So there
 *   is no pause button, and it stops for every reason that needs no control:
 *   a mouse over the row, keyboard focus inside the widget (hover cannot
 *   reach a keyboard), a finger on it, an open "Read more" (someone is
 *   reading), the row being mostly off screen, a hidden tab, or every card
 *   already fitting. Under prefers-reduced-motion it never moves on its own:
 *   the setting is the visitor's own control. Anything the visitor does (an
 *   arrow, a swipe, lifting a finger) restarts the full 8 s. The root says
 *   what it is doing in data-wc-tp-auto: "running", or the pause reasons.
 *   The slide is the motion tokens' on-screen move (--wc-ease-in-out over
 *   --wc-duration-drawer; an arrow press uses --wc-ease-out, so it answers
 *   at once), and the return from the end to the first card is a short fade
 *   rather than a rewind across every card.
 * - offers "Read more" on a review the clamp actually cut, and nowhere else.
 *   The whole text is always in the page; the button only changes what is on
 *   screen. Measured after the fonts settle, and again only if the width
 *   changes, so it costs nothing while scrolling.
 * - asks Trustpilot's own bootstrap (already on every theme) to draw a
 *   TrustBox it has not drawn, in the theme editor only, where a section is
 *   re-rendered after the bootstrap's one pass over the page.
 *
 * No third-party code is loaded here. INP: each handler sets an attribute,
 * starts one short scroll animation or restarts a timer; the only layout
 * reads are one getBoundingClientRect per step and the clamp measurement.
 */

const MAX_AGE_MS = 72 * 60 * 60 * 1000;
const FUTURE_SLACK_MS = 60 * 60 * 1000;
const ADVANCE_MS = 8000;
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

/* A CSS cubic-bezier as a function of progress, so the scripted slide uses
   the same curve as the motion tokens. */
function bezier(x1, y1, x2, y2) {
  const at = (a, b, t) => ((1 - 3 * b + 3 * a) * t + (3 * b - 6 * a)) * t * t + 3 * a * t;
  return (x) => {
    if (x <= 0 || x >= 1) return x <= 0 ? 0 : 1;
    let lo = 0;
    let hi = 1;
    let t = x;
    for (let i = 0; i < 24; i++) {
      const v = at(x1, x2, t);
      if (Math.abs(v - x) < 1e-5) break;
      if (v < x) lo = t;
      else hi = t;
      t = (lo + hi) / 2;
    }
    return at(y1, y2, t);
  };
}

function token(element, name, fallback) {
  return getComputedStyle(element).getPropertyValue(name).trim() || fallback;
}

function ms(value, fallback) {
  const n = parseFloat(value);
  if (Number.isNaN(n)) return fallback;
  return /ms\s*$/.test(value) ? n : /s\s*$/.test(value) ? n * 1000 : n;
}

export function summaryIsFresh(asOf, now = Date.now()) {
  const t = Date.parse(asOf);
  if (Number.isNaN(t)) return false;
  const age = now - t;
  return age <= MAX_AGE_MS && age >= -FUTURE_SLACK_MS;
}

class WcTpReviews extends HTMLElement {
  connectedCallback() {
    this.showSummary();

    this.list = this.querySelector("[data-wc-tp-list]");
    this.carousel = this.querySelector(".wc-tp__carousel");
    this.prev = this.querySelector("[data-wc-tp-prev]");
    this.next = this.querySelector("[data-wc-tp-next]");
    this.buttons = [...this.querySelectorAll(".wc-tp__more")];

    this.onClick = (event) => {
      const more = event.target.closest(".wc-tp__more");
      if (more) return this.toggleMore(more);
      if (event.target.closest("[data-wc-tp-prev]")) return this.step(-1, true);
      if (event.target.closest("[data-wc-tp-next]")) return this.step(1, true);
    };
    this.addEventListener("click", this.onClick);

    if (this.list) {
      const first = this.list.firstElementChild;
      const last = this.list.lastElementChild;
      if (first && last && this.prev && this.next) {
        this.edges = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              const button = entry.target === first ? this.prev : this.next;
              button.disabled = entry.intersectionRatio > 0.9;
            }
            const fits = this.prev.disabled && this.next.disabled;
            this.carousel?.toggleAttribute("data-wc-tp-fits", fits);
            this.pause("fits", fits);
          },
          { root: this.list, threshold: [0, 0.9, 1] },
        );
        this.edges.observe(first);
        this.edges.observe(last);
      }
    }

    const measure = () => this.measure();
    (document.fonts?.ready || Promise.resolve()).then(measure);
    this.lastWidth = 0;
    this.resize = new ResizeObserver(([entry]) => {
      const width = Math.round(entry.contentRect.width);
      if (width !== this.lastWidth) {
        this.lastWidth = width;
        measure();
      }
    });
    this.resize.observe(this);

    this.drawTrustBox();
    this.autoplay();
  }

  disconnectedCallback() {
    this.resize?.disconnect();
    this.edges?.disconnect();
    this.seen?.disconnect();
    this.removeEventListener("click", this.onClick);
    document.removeEventListener("visibilitychange", this.onVisibility);
    reduceMotion.removeEventListener?.("change", this.onMotion);
    window.removeEventListener("touchend", this.onTouchEnd);
    window.removeEventListener("touchcancel", this.onTouchEnd);
    clearTimeout(this.timer);
    clearTimeout(this.fadeTimer);
    cancelAnimationFrame(this.raf);
  }

  /* The auto-advance: one timer, which runs only while nothing pauses it. */
  autoplay() {
    this.pauses = new Set();
    if (!this.list || !this.next || !this.carousel) return;
    // Off screen until the observer first says otherwise.
    this.pauses.add("offscreen");

    this.carousel.addEventListener("pointerenter", (event) => {
      if (event.pointerType === "mouse") this.pause("hover", true);
    });
    this.carousel.addEventListener("pointerleave", (event) => {
      if (event.pointerType === "mouse") this.pause("hover", false);
    });

    // Keyboard focus only: a mouse click on an arrow must not stop it for good.
    this.addEventListener("focusin", (event) => {
      if (event.target.matches(":focus-visible")) this.pause("focus", true);
    });
    this.addEventListener("focusout", (event) => {
      if (!event.relatedTarget || !this.contains(event.relatedTarget)) this.pause("focus", false);
    });

    // A finger on the row. Touch events, not pointer events: the browser
    // cancels the pointer the moment it takes over the swipe.
    this.onTouchEnd = () => this.pause("touch", false);
    this.list.addEventListener(
      "touchstart",
      () => {
        this.stopSlide();
        this.pause("touch", true);
      },
      { passive: true },
    );
    window.addEventListener("touchend", this.onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", this.onTouchEnd, { passive: true });
    this.list.addEventListener("wheel", () => this.stopSlide(), { passive: true });

    // A scroll this did not make (a swipe, a trackpad) restarts the clock.
    this.list.addEventListener(
      "scroll",
      () => {
        if (!this.sliding) this.schedule();
      },
      { passive: true },
    );

    this.seen = new IntersectionObserver(([entry]) => this.pause("offscreen", entry.intersectionRatio < 0.5), {
      threshold: [0, 0.5, 1],
    });
    this.seen.observe(this.carousel);

    this.onVisibility = () => this.pause("hidden", document.hidden);
    document.addEventListener("visibilitychange", this.onVisibility);
    this.onVisibility();

    this.onMotion = () => this.pause("motion", reduceMotion.matches);
    reduceMotion.addEventListener?.("change", this.onMotion);
    this.onMotion();
    this.schedule();
  }

  /* Only a CHANGE restarts the clock. Observers repeat themselves (the
     arrows' edge observer reports on every swipe), and a repeat is not
     something the visitor did. */
  pause(reason, on) {
    if (!this.pauses || this.pauses.has(reason) === Boolean(on)) return;
    if (on) this.pauses.add(reason);
    else this.pauses.delete(reason);
    this.schedule();
  }

  /* (Re)starts the full interval, or stops it while anything pauses it. */
  schedule() {
    clearTimeout(this.timer);
    this.timer = null;
    if (!this.pauses) return;
    this.dataset.wcTpAuto = this.pauses.size ? [...this.pauses].sort().join(" ") : "running";
    if (this.pauses.size || this.sliding) return;
    this.timer = setTimeout(() => this.advance(), ADVANCE_MS);
  }

  advance() {
    this.timer = null;
    if (this.pauses.size || this.sliding) return;
    if (this.next.disabled) this.rewind();
    else this.step(1, false);
  }

  /* Where card `index` sits when it is the first one in view: its own
     offset, so a move always ends on a snap point, even when it starts
     between cards (a trackpad rests anywhere under "proximity"). */
  cardOffset(index) {
    const cards = this.list.children;
    const first = cards[0];
    const card = cards[Math.max(0, Math.min(index, cards.length - 1))];
    return Math.abs(card.offsetLeft - first.offsetLeft);
  }

  /* The last page back to the first: a fade out, a jump, a fade in. A
     rewind across every card would be the longest, fastest move on the
     page, for no information. */
  rewind() {
    const list = this.list;
    const out = ms(token(this, "--wc-duration-press", "150ms"), 150);
    const back = ms(token(this, "--wc-duration", "200ms"), 200);
    const easing = token(this, "--wc-ease-out", "cubic-bezier(0.23, 1, 0.32, 1)");
    this.sliding = true;
    this.rewinding = true;
    const fade = list.animate?.([{ opacity: 1 }, { opacity: 0 }], { duration: out, easing, fill: "forwards" });
    this.fadeTimer = setTimeout(() => {
      list.scrollTo({ left: 0, behavior: "instant" });
      fade?.cancel();
      list.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: back, easing });
      this.rewinding = false;
      this.sliding = false;
      this.schedule();
    }, out);
  }

  stopSlide() {
    // A rewind is a fade, over in 150 ms: let it finish.
    if (!this.sliding || this.rewinding) return;
    cancelAnimationFrame(this.raf);
    this.endSlide();
  }

  endSlide() {
    this.list.style.removeProperty("scroll-snap-type");
    this.list.style.removeProperty("scroll-behavior");
    this.sliding = false;
  }

  showSummary() {
    const template = this.querySelector("template[data-wc-tp-summary-template]");
    const slot = this.querySelector("[data-wc-tp-summary-slot]");
    if (!template || !slot) return;
    if (summaryIsFresh(template.dataset.asOf)) {
      if (!slot.firstElementChild) slot.append(template.content.cloneNode(true));
      this.dataset.wcTpSummary = "shown";
    } else {
      slot.replaceChildren();
      this.dataset.wcTpSummary = "stale";
    }
  }

  /* An arrow (byVisitor) moves a page; the timer moves ONE card, as
     EmbedSocial's Swiper does. A page is ~1,100 px on a computer, and that
     in 250 ms is a whip; a card is a calm step inside the plan's cap. The
     move is scripted rather than scrollBy's "smooth", whose curve and length
     belong to the browser, and snapping is held off while it runs, so each
     frame is not pulled to a card. A press answers at once (ease-out); the
     timer's own move eases in and out, as a thing moving across the screen
     does. */
  step(direction, byVisitor) {
    const list = this.list;
    const card = list?.firstElementChild;
    if (!card) return;
    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    const width = card.getBoundingClientRect().width + gap;
    const visible = Math.max(1, Math.floor((list.clientWidth + gap) / width));
    const rtl = getComputedStyle(this).direction === "rtl" ? -1 : 1;
    const max = list.scrollWidth - list.clientWidth;
    const from = list.scrollLeft;
    const at = Math.round(Math.abs(from) / width);
    const by = byVisitor ? visible : 1;
    const offset = Math.min(this.cardOffset(at + direction * by), max);
    const target = rtl * Math.max(0, offset);

    cancelAnimationFrame(this.raf);
    clearTimeout(this.timer);
    if (reduceMotion.matches || Math.abs(target - from) < 1) {
      list.scrollTo({ left: target, behavior: "instant" });
      this.sliding = false;
      if (byVisitor) this.schedule();
      return;
    }

    const duration = ms(token(this, "--wc-duration-drawer", "250ms"), 250);
    const curve = (byVisitor
      ? token(this, "--wc-ease-out", "cubic-bezier(0.23, 1, 0.32, 1)")
      : token(this, "--wc-ease-in-out", "cubic-bezier(0.77, 0, 0.175, 1)")
    ).match(/-?[\d.]+/g) || [0.77, 0, 0.175, 1];
    const ease = bezier(...curve.slice(0, 4).map(Number));
    list.style.setProperty("scroll-snap-type", "none");
    list.style.setProperty("scroll-behavior", "auto");
    this.sliding = true;
    let start = null;
    const frame = (now) => {
      if (start === null) start = now;
      const t = Math.min(1, (now - start) / duration);
      list.scrollLeft = from + (target - from) * ease(t);
      if (t < 1) {
        this.raf = requestAnimationFrame(frame);
        return;
      }
      this.endSlide();
      this.schedule();
    };
    this.raf = requestAnimationFrame(frame);
  }

  // A08 (Luke, 14 Oct): "Read more" opens the whole review in a popup rather
  // than growing the card, so the row keeps one height. The review's own markup
  // is copied in (ids stripped, its button dropped); the carousel pauses while
  // it is open, exactly as it did for an expanded card.
  toggleMore(button) {
    const review = button.closest("[data-wc-tp-review]");
    const dialog = this.querySelector("[data-wc-tp-dialog]");
    const body = dialog && dialog.querySelector("[data-wc-tp-dialog-body]");
    if (!review || !dialog || !body || typeof dialog.showModal !== "function") return;
    const card = (review.querySelector("figure") || review).cloneNode(true);
    card.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
    card.querySelectorAll(".wc-tp__more").forEach((el) => el.remove());
    body.replaceChildren(card);
    if (!dialog.dataset.wired) {
      dialog.dataset.wired = "1";
      dialog.addEventListener("close", () => {
        body.replaceChildren();
        this.pause("reading", false);
      });
      dialog.addEventListener("click", (event) => {
        if (event.target === dialog || event.target.closest("[data-wc-tp-close]")) dialog.close();
      });
    }
    this.pause("reading", true);
    dialog.showModal();
  }

  measure() {
    for (const button of this.buttons) {
      const review = button.closest("[data-wc-tp-review]");
      if (review.hasAttribute("data-expanded")) continue;
      const body = review.querySelector(".wc-tp__body");
      button.hidden = !(body && body.scrollHeight > body.clientHeight + 2);
    }
  }

  drawTrustBox() {
    const box = this.querySelector(".trustpilot-widget");
    if (!box || box.querySelector("iframe")) return;
    const draw = () => {
      if (
        window.Trustpilot &&
        typeof window.Trustpilot.loadFromElement === "function" &&
        !box.querySelector("iframe")
      ) {
        window.Trustpilot.loadFromElement(box, true);
      }
    };
    if (window.Shopify && window.Shopify.designMode) {
      if (document.readyState === "complete") draw();
      else window.addEventListener("load", draw, { once: true });
    }
  }
}

if (!customElements.get("wc-tp-reviews")) customElements.define("wc-tp-reviews", WcTpReviews);
